import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import {
  api,
  type CompanyDetail,
  type CompanyMessage,
  type Page,
  type Unit,
} from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  Spinner,
  SortableTh,
  Table,
  TableFooter,
  Tabs,
  Td,
  Textarea,
  Th,
  Toolbar,
} from "@/components/ui";
import { useClientTable, useServerTable } from "@/lib/table";
import { useAuth, useMayUse } from "@/app/auth";
import { AudioFiles } from "./audiofiles";
import { CompanyAuth } from "./companyauth";
import { CompanySpeech } from "./speech";

// --- list ----------------------------------------------------------------

export function CompaniesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = useAuth();
  const isGlobal = me?.role === "global_admin";

  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  // The list can run to hundreds of customers, so the server does the paging
  // and the ordering; the table only says which page in which order.
  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "name", dir: "asc" },
    resetOn: [search],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["companies", "list", search, table.query],
    queryFn: () =>
      api<Page<CompanyDetail>>(
        `/api/admin/companies?${table.query}&q=${encodeURIComponent(search)}`,
      ),
  });

  // The total comes back with the page, and the footer needs it to say how
  // many pages there are.
  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  const create = useMutation({
    mutationFn: (name: string) =>
      api<CompanyDetail>("/api/admin/companies", {
        method: "POST",
        body: emptyCompany(name),
      }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setCreating(false);
      navigate(`/companies/${c.id}`);
    },
  });

  return (
    <>
      <PageHeader
        title="Firmen"
        description="Die Kunden und ihre Konfiguration."
        actions={
          isGlobal ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden /> Firma anlegen
            </Button>
          ) : undefined
        }
      />

      <Toolbar>
        <Input
          className="w-64"
          placeholder="Name oder Domain suchen"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {data && data.total === 0 && (
          <span className="text-sm muted">keine Treffer</span>
        )}
      </Toolbar>

      {error && <ErrorNote>{(error as Error).message}</ErrorNote>}
      {isLoading ? (
        <Spinner />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <SortableTh table={table} sortKey="name">
                  Name
                </SortableTh>
                <SortableTh table={table} sortKey="domain">
                  Domain
                </SortableTh>
                <SortableTh table={table} sortKey="billingAccount">
                  BAN
                </SortableTh>
                <SortableTh
                  table={table}
                  sortKey="userCount"
                  first="desc"
                  align="right"
                >
                  Benutzer
                </SortableTh>
                <SortableTh table={table} sortKey="notifyChannel">
                  Benachrichtigung
                </SortableTh>
                <SortableTh table={table} sortKey="active" first="desc">
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer hover:bg-[var(--surface-sunken)]"
                  onClick={() => navigate(`/companies/${c.id}`)}
                >
                  <Td className="font-medium">{c.name}</Td>
                  <Td className="muted">{c.domain}</Td>
                  <Td className="tabular-nums">{c.billingAccount || "—"}</Td>
                  <Td className="text-right tabular-nums">{c.userCount ?? 0}</Td>
                  <Td>{channelLabel(c.notifyChannel)}</Td>
                  <Td>
                    {c.active ? (
                      <Badge tone="success">aktiv</Badge>
                    ) : (
                      <Badge tone="neutral">stillgelegt</Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {(data?.items ?? []).length === 0 && (
            <Empty title="Keine Firma gefunden" />
          )}
          <TableFooter table={table} noun="Firmen" />
        </Card>
      )}

      <NewCompanyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={(name) => create.mutate(name)}
        error={create.error as Error | null}
        busy={create.isPending}
      />
    </>
  );
}

function NewCompanyDialog({
  open,
  onClose,
  onCreate,
  error,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
  error: Error | null;
  busy: boolean;
}) {
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onClose={onClose} title="Firma anlegen">
      <div className="flex flex-col gap-4">
        <Field
          label="Name"
          hint="Die Domain wird daraus abgeleitet und ist der Schlüssel gegenüber der Central-API."
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        {error && <ErrorNote>{error.message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || busy}
            onClick={() => onCreate(name.trim())}
          >
            Anlegen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function emptyCompany(name: string): Partial<CompanyDetail> {
  return {
    name,
    notifyChannel: "none",
    welcomeMailTrigger: "never",
    mailTransport: "smtp",
    proxySource: "users",
    defaultMfaMode: "off",
    active: true,
  };
}

// --- detail --------------------------------------------------------------

export function CompanyDetailPage() {
  const { id } = useParams();
  const companyId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = useAuth();
  const isGlobal = me?.role === "global_admin";
  // Die Synchronisation ist die Leitung zum Netzbetreiber und gehört dem
  // Betreiber; sie von hier aus anzustoßen ist derselbe Bereich wie die Seite.
  const maySync = useMayUse("sync");

  const [tab, setTab] = useState("stammdaten");
  const [deleting, setDeleting] = useState(false);
  const [copying, setCopying] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => api<CompanyDetail>(`/api/admin/companies/${companyId}/detail`),
    enabled: Number.isFinite(companyId),
  });

  const remove = useMutation({
    mutationFn: () => api<void>(`/api/admin/companies/${companyId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies"] });
      navigate("/companies");
    },
  });

  const copy = useMutation({
    mutationFn: (name: string) =>
      api<CompanyDetail>(`/api/admin/companies/${companyId}/copy`, {
        method: "POST",
        body: { name },
      }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setCopying(false);
      navigate(`/companies/${c.id}`);
    },
  });

  const fullSync = useMutation({
    mutationFn: () =>
      api<{ queued: number }>(`/api/admin/companies/${companyId}/sync`, { method: "POST" }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.domain}
        actions={
          <>
            {maySync && (
              <Button onClick={() => fullSync.mutate()} disabled={!data.syncEnabled}>
                Voll-Sync
              </Button>
            )}
            {isGlobal && (
              <>
                <Button onClick={() => setCopying(true)}>
                  <Copy className="size-4" aria-hidden /> Kopieren
                </Button>
                <Button variant="danger" onClick={() => setDeleting(true)}>
                  <Trash2 className="size-4" aria-hidden /> Löschen
                </Button>
              </>
            )}
          </>
        }
      />

      {fullSync.isSuccess && (
        <div className="mb-4">
          <Notice tone="success">
            {fullSync.data.queued} Änderungen zur Synchronisation eingereiht.
          </Notice>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "stammdaten", label: "Stammdaten" },
          { id: "texte", label: "Benachrichtigungen" },
          { id: "abrechnung", label: "Abrechnung" },
          { id: "struktur", label: "Abteilungen & Funktionen" },
          { id: "ansagen", label: "Ansagen" },
          { id: "audio", label: "Audiodateien" },
          { id: "sprache", label: "Sprachsynthese" },
          // Nur für den globalen Administrator: der Issuer ist eine Adresse,
          // die der Server abruft, und die Routen dahinter lassen sonst
          // niemanden zu.
          ...(isGlobal ? [{ id: "anmeldung", label: "Anmeldung" }] : []),
        ]}
      />

      {tab === "stammdaten" && <CompanyForm company={data} section="stammdaten" />}
      {tab === "texte" && <CompanyForm company={data} section="texte" />}
      {tab === "abrechnung" && <CompanyForm company={data} section="abrechnung" />}
      {tab === "struktur" && <Structure companyId={companyId} />}
      {tab === "ansagen" && <CompanyMessages companyId={companyId} />}
      {tab === "audio" && <AudioFiles companyId={companyId} />}
      {tab === "sprache" && <CompanySpeech companyId={companyId} />}
      {tab === "anmeldung" && isGlobal && <CompanyAuth companyId={companyId} />}

      <ConfirmDialog
        open={deleting}
        title="Firma löschen"
        message={
          <>
            <p>
              <strong>{data.name}</strong> und alles, was dazu gehört — Benutzer,
              Ansagen, Abteilungen, Aufnahmen — wird gelöscht. Das lässt sich
              nicht rückgängig machen.
            </p>
            <p className="mt-2 muted">
              Soll die Firma nur nicht mehr betrieben werden, genügt es, sie auf
              „stillgelegt" zu setzen: die Historie bleibt, es wird nichts mehr
              eingespielt und nichts mehr abgerechnet.
            </p>
          </>
        }
        challenge={data.name}
        challengeLabel="Zur Bestätigung den Namen der Firma eingeben"
        error={remove.error ? (remove.error as Error).message : null}
        onConfirm={() => remove.mutate()}
        onClose={() => {
          setDeleting(false);
          remove.reset();
        }}
        busy={remove.isPending}
      />

      <CopyDialog
        open={copying}
        onClose={() => setCopying(false)}
        onCopy={(name) => copy.mutate(name)}
        busy={copy.isPending}
        error={copy.error as Error | null}
      />
    </>
  );
}

function CopyDialog({
  open,
  onClose,
  onCopy,
  busy,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onCopy: (name: string) => void;
  busy: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onClose={onClose} title="Firma kopieren">
      <div className="flex flex-col gap-4">
        <Notice>
          Kopiert wird die Konfiguration: Abteilungen, Funktionen, Ansagen und
          deren Zuordnung. Benutzer und laufende Ansagen werden nicht kopiert,
          ebenso wenig BAN und Abrechnung.
        </Notice>
        <Field label="Name der neuen Firma">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        {error && <ErrorNote>{error.message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || busy}
            onClick={() => onCopy(name.trim())}
          >
            Kopieren
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- the editing form ----------------------------------------------------

function CompanyForm({
  company,
  section,
}: {
  company: CompanyDetail;
  section: "stammdaten" | "texte" | "abrechnung";
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<CompanyDetail>(company);
  const set = <K extends keyof CompanyDetail>(key: K, value: CompanyDetail[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Die Ansagearten dieser Firma — für die Auswahl, welche eine Besprechung
  // erzeugt. Nur im Stammdaten-Abschnitt geladen, wo sie gebraucht wird.
  const messages = useQuery({
    queryKey: ["company", company.id, "messages"],
    queryFn: () => api<CompanyMessage[]>(`/api/admin/companies/${company.id}/messages`),
    enabled: section === "stammdaten",
  });

  const save = useMutation({
    mutationFn: () =>
      api<CompanyDetail>(`/api/admin/companies/${company.id}`, {
        method: "PUT",
        body: draft,
      }),
    onSuccess: (c) => {
      qc.setQueryData(["company", company.id], c);
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setDraft(c);
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(company);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Card className="p-5">
        {section === "stammdaten" && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name">
              <Input value={draft.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field
              label="Domain"
              hint="Schlüssel gegenüber der Central-API. Ändern bricht die Zuordnung dort."
            >
              <Input value={draft.domain} onChange={(e) => set("domain", e.target.value)} />
            </Field>
            <Field label="BAN" hint="Rahmenvertragsnummer für die Abrechnung.">
              <Input
                value={draft.billingAccount}
                onChange={(e) => set("billingAccount", e.target.value)}
              />
            </Field>
            <Field label="Connector-URL">
              <Input
                value={draft.connectorUrl}
                onChange={(e) => set("connectorUrl", e.target.value)}
              />
            </Field>
            <Field label="Vertretung aus">
              <Select
                value={draft.proxySource}
                onChange={(e) =>
                  set("proxySource", e.target.value as CompanyDetail["proxySource"])
                }
              >
                <option value="users">Mitarbeitern</option>
                <option value="secretariats">Sekretariaten</option>
              </Select>
            </Field>
            <Field
              label="Zwei-Faktor-Voreinstellung"
              hint="Welches Verfahren der Einrichtungsassistent Administratoren dieser Firma vorschlägt. Ob ein zweiter Faktor nötig ist, entscheidet die Rolle."
            >
              <Select
                value={draft.defaultMfaMode}
                onChange={(e) => set("defaultMfaMode", e.target.value)}
              >
                <option value="totp">Authenticator-App</option>
                <option value="sms">SMS</option>
                <option value="off">keine Vorauswahl</option>
              </Select>
            </Field>
            <Field
              label="Passwortregel"
              hint="Regulärer Ausdruck ohne Begrenzer. Leer heißt: mindestens zwölf Zeichen."
            >
              <Input
                value={draft.passwordPolicyRegex}
                onChange={(e) => set("passwordPolicyRegex", e.target.value)}
              />
            </Field>
            <Field label="Hinweis zur Passwortregel">
              <Input
                value={draft.passwordPolicyHint}
                onChange={(e) => set("passwordPolicyHint", e.target.value)}
              />
            </Field>
            <Field label="Farbe (mobil)">
              <Input
                value={draft.colorMobile}
                onChange={(e) => set("colorMobile", e.target.value)}
                placeholder="#0f172a"
              />
            </Field>
            <Field label="Logo (mobil)">
              <Input
                value={draft.logoMobileKey}
                onChange={(e) => set("logoMobileKey", e.target.value)}
              />
            </Field>

            <div className="sm:col-span-2 grid gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
              <Checkbox
                label="Kalender-Ansagen"
                hint="Wer die Einladungsadresse zu einer Besprechung einlädt, bekommt für deren Dauer die unten gewählte Ansage auf die Mobilbox — und sie wird wieder abgenommen, wenn die Besprechung verschoben oder abgesagt wird."
                checked={draft.calendarSyncEnabled}
                onChange={(e) => set("calendarSyncEnabled", e.target.checked)}
              />
              <Field
                label="Ansage einer Besprechung"
                hint="Ohne diese Angabe entsteht aus einer Einladung nichts. Die Standardansage ist hier nicht wählbar: sie ist die Ansage für „niemand ist abwesend“."
              >
                <Select
                  className="max-w-md"
                  value={String(draft.calendarMessageId || "")}
                  onChange={(e) => set("calendarMessageId", Number(e.target.value))}
                >
                  <option value="">— keine —</option>
                  {(messages.data ?? [])
                    .filter((m) => !m.isDefault && m.active)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name || m.templateName}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field
                label="Benachrichtigung bei Kalender-Ansagen"
                hint="Besprechungen sind viele und kurz; standardmäßig wird niemand benachrichtigt."
              >
                <Select
                  className="max-w-xs"
                  value={draft.calendarNotify || "none"}
                  onChange={(e) => set("calendarNotify", e.target.value)}
                >
                  <option value="none">keine</option>
                  <option value="self">die Person selbst</option>
                  <option value="self_and_proxy">Person und Vertretung</option>
                </Select>
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Checkbox
                label="Synchronisation aktiv"
                hint="Änderungen an dieser Firma werden an Keycloak und die Central-API gemeldet."
                checked={draft.syncEnabled}
                onChange={(e) => set("syncEnabled", e.target.checked)}
              />
              <Checkbox
                label="Firma aktiv"
                hint="Stillgelegte Firmen werden nicht abgerechnet und nicht mehr bearbeitet."
                checked={draft.active}
                onChange={(e) => set("active", e.target.checked)}
              />
            </div>
          </div>
        )}

        {section === "texte" && (
          <div className="grid gap-3">
            <Field
              label="Benachrichtigungsweg"
              hint="Wie die Person erfährt, dass ihre Ansage aktiv ist."
            >
              <Select
                className="max-w-xs"
                value={draft.notifyChannel}
                onChange={(e) =>
                  set("notifyChannel", e.target.value as CompanyDetail["notifyChannel"])
                }
              >
                <option value="none">keine</option>
                <option value="sms">SMS</option>
                <option value="email">E-Mail</option>
              </Select>
            </Field>

            <Field label="Aktivierung, mit Vertretung">
              <Textarea
                value={draft.textActivateWithProxy}
                onChange={(e) => set("textActivateWithProxy", e.target.value)}
              />
            </Field>
            <Field label="Aktivierung, ohne Vertretung">
              <Textarea
                value={draft.textActivateWithoutProxy}
                onChange={(e) => set("textActivateWithoutProxy", e.target.value)}
              />
            </Field>
            <Field label="Aktivierung, an die Vertretung">
              <Textarea
                value={draft.textActivateProxy}
                onChange={(e) => set("textActivateProxy", e.target.value)}
              />
            </Field>
            <Field label="Beendigung">
              <Textarea
                value={draft.textDeactivate}
                onChange={(e) => set("textDeactivate", e.target.value)}
              />
            </Field>
            <Field label="Beendigung, an die Vertretung">
              <Textarea
                value={draft.textDeactivateProxy}
                onChange={(e) => set("textDeactivateProxy", e.target.value)}
              />
            </Field>

            <hr style={{ borderColor: "var(--border)" }} />

            <Field label="Willkommensmail: Betreff">
              <Input
                value={draft.welcomeMailSubject}
                onChange={(e) => set("welcomeMailSubject", e.target.value)}
              />
            </Field>
            <Field
              label="Willkommensmail: Text"
              hint="Platzhalter: {ANREDE} {VORNAME} {NAME} {EMAIL} {PASSWORT}"
            >
              <Textarea
                className="min-h-40"
                value={draft.welcomeMailBody}
                onChange={(e) => set("welcomeMailBody", e.target.value)}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Absender">
                <Input
                  value={draft.welcomeMailSender}
                  onChange={(e) => set("welcomeMailSender", e.target.value)}
                />
              </Field>
              <Field label="Versandzeitpunkt">
                <Select
                  value={draft.welcomeMailTrigger}
                  onChange={(e) => set("welcomeMailTrigger", e.target.value)}
                >
                  <option value="never">nie</option>
                  <option value="on_create">beim Anlegen</option>
                  <option value="manual">nur manuell</option>
                </Select>
              </Field>
              <Field label="Versandweg">
                <Select
                  value={draft.mailTransport}
                  onChange={(e) => set("mailTransport", e.target.value)}
                >
                  <option value="smtp">SMTP</option>
                  <option value="connector">Mail-Connector</option>
                </Select>
              </Field>
            </div>
          </div>
        )}

        {section === "abrechnung" && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Checkbox
                label="Abrechnung aktiv"
                hint="Ohne diesen Haken wird für diese Firma nichts an A1 gemeldet."
                checked={draft.billingEnabled}
                onChange={(e) => set("billingEnabled", e.target.checked)}
              />
            </div>
            <Field label="Preis je Benutzer">
              <Input
                type="number"
                step="0.01"
                value={draft.pricePerUser}
                onChange={(e) => set("pricePerUser", Number(e.target.value))}
              />
            </Field>
            <Field label="Grundgebühr">
              <Input
                type="number"
                step="0.01"
                value={draft.priceCompanyBase}
                onChange={(e) => set("priceCompanyBase", Number(e.target.value))}
              />
            </Field>
            <Field
              label="Mindestabnahme"
              hint="Wird die Zahl abrechenbarer Benutzer unterschritten, wird die Differenz verrechnet."
            >
              <Input
                type="number"
                value={draft.minimumUsers}
                onChange={(e) => set("minimumUsers", Number(e.target.value))}
              />
            </Field>
            <Field label="Text auf der Benutzerposition">
              <Input
                value={draft.billingTextUser}
                onChange={(e) => set("billingTextUser", e.target.value)}
              />
            </Field>
            <Field label="Text auf der Firmenposition">
              <Input
                value={draft.billingTextCompany}
                onChange={(e) => set("billingTextCompany", e.target.value)}
              />
            </Field>
          </div>
        )}

        {save.error && (
          <div className="mt-4">
            <ErrorNote>{(save.error as Error).message}</ErrorNote>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={!dirty || save.isPending}>
            Speichern
          </Button>
          {dirty && <span className="text-xs muted">Ungespeicherte Änderungen</span>}
          {save.isSuccess && !dirty && (
            <span className="text-xs muted">Gespeichert</span>
          )}
        </div>
      </Card>
    </form>
  );
}

// --- departments and positions -------------------------------------------

function Structure({ companyId }: { companyId: number }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <UnitList companyId={companyId} kind="departments" title="Abteilungen" />
      <UnitList companyId={companyId} kind="positions" title="Funktionen" />
    </div>
  );
}

function UnitList({
  companyId,
  kind,
  title,
}: {
  companyId: number;
  kind: "departments" | "positions";
  title: string;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [removing, setRemoving] = useState<Unit | null>(null);

  const key = ["company", companyId, kind];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<Unit[]>(`/api/admin/companies/${companyId}/${kind}`),
  });

  // A company's structure arrives complete, so the browser orders and pages
  // it: a round trip for twelve departments would be silly.
  const table = useClientTable(data, {
    initial: { key: "name", dir: "asc" },
    pageSize: 10,
  });

  const create = useMutation({
    mutationFn: () =>
      api<Unit>(`/api/admin/companies/${companyId}/${kind}`, {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/${kind === "departments" ? "departments" : "positions"}/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  return (
    <Card className="p-4">
      <h2 className="mb-3 font-medium">{title}</h2>

      {isLoading ? (
        <Spinner />
      ) : (
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="name">
                Name
              </SortableTh>
              <SortableTh
                table={table}
                sortKey="userCount"
                first="desc"
                align="right"
              >
                Benutzer
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((u) => (
              <tr key={u.id}>
                <Td>{u.name}</Td>
                <Td className="text-right tabular-nums">{u.userCount}</Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(u)}>
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {(data ?? []).length === 0 && !isLoading && <Empty title="Noch nichts angelegt" />}
      <TableFooter table={table} noun={title} />

      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <Input
          placeholder={`${title.replace(/en$/, "")} hinzufügen`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          <Plus className="size-4" aria-hidden />
        </Button>
      </form>
      {create.error && (
        <div className="mt-2">
          <ErrorNote>{(create.error as Error).message}</ErrorNote>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={`${title.replace(/en$/, "")} löschen`}
        message={
          <>
            <strong>{removing?.name}</strong> wird gelöscht.{" "}
            {removing?.userCount ? (
              <>
                {removing.userCount} Benutzer verlieren die Zuordnung und behalten
                sonst alles.
              </>
            ) : null}
          </>
        }
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </Card>
  );
}

// --- the company's announcements -----------------------------------------

function CompanyMessages({ companyId }: { companyId: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CompanyMessage | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["company", companyId, "messages"],
    queryFn: () => api<CompanyMessage[]>(`/api/admin/companies/${companyId}/messages`),
  });

  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api<{ id: number; name: string }[]>("/api/admin/templates"),
  });

  const save = useMutation({
    mutationFn: (m: CompanyMessage) =>
      m.id
        ? api<CompanyMessage>(`/api/admin/company-messages/${m.id}`, {
            method: "PUT",
            body: m,
          })
        : api<CompanyMessage>(`/api/admin/companies/${companyId}/messages`, {
            method: "POST",
            body: m,
          }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["company", companyId, "messages"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/company-messages/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["company", companyId, "messages"] }),
  });

  // The announcements a company offers are a short, complete list, so the
  // ordering happens here. "in Verwendung" is the one worth turning around:
  // it answers which of them may be removed.
  const table = useClientTable(data, {
    initial: { key: "name", dir: "asc" },
    accessors: {
      name: (m) => m.name || m.templateName || "",
      dateMode: (m) => dateModeLabel(m.dateMode ?? ""),
      notify: (m) =>
        m.notifyAllowed
          ? m.notifyOverridable
            ? "wählbar"
            : notifyLabel(m.notifyDefault)
          : "keine",
      inUse: (m) => m.inUse ?? 0,
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <Card className="p-4">
      <Toolbar>
        <Button
          variant="primary"
          onClick={() =>
            setEditing({
              id: 0,
              companyId,
              templateId: templates.data?.[0]?.id ?? 0,
              name: "",
              subtitle: "",
              body: "",
              backgroundFadeAt: 0,
              isDefault: false,
              notifyAllowed: true,
              notifyDefault: "none",
              notifyOverridable: true,
              endLabelInclusive: false,
              active: true,
            })
          }
        >
          <Plus className="size-4" aria-hidden /> Ansage hinzufügen
        </Button>
      </Toolbar>

      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="name">
              Name
            </SortableTh>
            <SortableTh table={table} sortKey="templateName">
              Bauplan
            </SortableTh>
            <SortableTh table={table} sortKey="dateMode">
              Datum
            </SortableTh>
            <SortableTh table={table} sortKey="notify">
              Benachrichtigung
            </SortableTh>
            <SortableTh table={table} sortKey="inUse" first="desc" align="right">
              in Verwendung
            </SortableTh>
            <Th />
          </tr>
        </thead>
        <tbody>
          {table.rows.map((m) => (
            <tr key={m.id}>
              <Td className="font-medium">
                {m.name || m.templateName}{" "}
                {m.isDefault && <Badge tone="info">Standardansage</Badge>}
                {!m.active && <Badge>inaktiv</Badge>}
              </Td>
              <Td className="muted">{m.templateName}</Td>
              <Td>{dateModeLabel(m.dateMode ?? "")}</Td>
              <Td>
                {m.notifyAllowed
                  ? m.notifyOverridable
                    ? "wählbar"
                    : notifyLabel(m.notifyDefault)
                  : "keine"}
              </Td>
              <Td className="text-right tabular-nums">{m.inUse ?? 0}</Td>
              <Td className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Ansageart bearbeiten"
                  title="Ansageart bearbeiten"
                  onClick={() => setEditing(m)}
                >
                  <Pencil className="size-4" aria-hidden />
                </Button>{" "}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove.mutate(m.id)}
                  disabled={(m.inUse ?? 0) > 0}
                  title={
                    (m.inUse ?? 0) > 0
                      ? "Es gibt geplante oder laufende Ansagen dieser Art"
                      : undefined
                  }
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(data ?? []).length === 0 && <Empty title="Diese Firma bietet noch keine Ansagen an" />}
      <TableFooter table={table} noun="Ansagen" />
      {remove.error && <ErrorNote>{(remove.error as Error).message}</ErrorNote>}

      {editing && (
        <CompanyMessageDialog
          value={editing}
          templates={templates.data ?? []}
          onClose={() => setEditing(null)}
          onSave={(m) => save.mutate(m)}
          busy={save.isPending}
          error={save.error as Error | null}
        />
      )}
    </Card>
  );
}

function CompanyMessageDialog({
  value,
  templates,
  onClose,
  onSave,
  busy,
  error,
}: {
  value: CompanyMessage;
  templates: { id: number; name: string }[];
  onClose: () => void;
  onSave: (m: CompanyMessage) => void;
  busy: boolean;
  error: Error | null;
}) {
  const [draft, setDraft] = useState(value);
  const set = <K extends keyof CompanyMessage>(k: K, v: CompanyMessage[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Dialog open onClose={onClose} title={value.id ? "Ansage bearbeiten" : "Ansage anlegen"}>
      <div className="flex flex-col gap-4">
        <Field label="Bauplan">
          <Select
            value={draft.templateId}
            onChange={(e) => set("templateId", Number(e.target.value))}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Name" hint="Leer: der Name des Bauplans wird verwendet.">
          <Input value={draft.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Untertitel">
          <Input value={draft.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
        </Field>
        <Field label="Text" hint="Was die Person im Portal und in der App liest.">
          <Textarea value={draft.body} onChange={(e) => set("body", e.target.value)} />
        </Field>
        <Field
          label="Hintergrundmusik ausblenden ab (Sekunden)"
          hint="0 heißt: keine Hintergrundmusik."
        >
          <Input
            type="number"
            step="0.1"
            value={draft.backgroundFadeAt}
            onChange={(e) => set("backgroundFadeAt", Number(e.target.value))}
          />
        </Field>

        <div>
          <Checkbox
            label="Standardansage dieser Firma"
            hint="Wird nach dem Ende einer Abwesenheit wieder eingespielt. Nur eine je Firma."
            checked={draft.isDefault}
            onChange={(e) => set("isDefault", e.target.checked)}
          />
          <Checkbox
            label="Benachrichtigung möglich"
            checked={draft.notifyAllowed}
            onChange={(e) => set("notifyAllowed", e.target.checked)}
          />
          <Checkbox
            label="Benachrichtigung wählbar"
            hint="Ohne diesen Haken gilt die Voreinstellung fest."
            checked={draft.notifyOverridable}
            onChange={(e) => set("notifyOverridable", e.target.checked)}
          />
          <Checkbox
            label="Enddatum ist inklusive"
            hint={
              draft.endLabelInclusive
                ? "Beschriftung: Abwesend bis inklusive"
                : "Beschriftung: Erreichbar AB"
            }
            checked={draft.endLabelInclusive}
            onChange={(e) => set("endLabelInclusive", e.target.checked)}
          />
          <Checkbox
            label="Aktiv"
            checked={draft.active}
            onChange={(e) => set("active", e.target.checked)}
          />
        </div>

        <Field label="Voreingestellte Benachrichtigung">
          <Select
            value={draft.notifyDefault}
            onChange={(e) => set("notifyDefault", e.target.value)}
          >
            <option value="none">keine</option>
            <option value="self">die Person</option>
            <option value="self_and_proxy">die Person und die Vertretung</option>
          </Select>
        </Field>

        {error && <ErrorNote>{error.message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={busy} onClick={() => onSave(draft)}>
            Speichern
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- labels --------------------------------------------------------------

export function channelLabel(channel: string): string {
  switch (channel) {
    case "sms":
      return "SMS";
    case "email":
      return "E-Mail";
    default:
      return "keine";
  }
}

export function dateModeLabel(mode: string): string {
  switch (mode) {
    case "end_required":
      return "Enddatum nötig";
    case "end_optional":
      return "Enddatum optional";
    case "minutes_only":
      return "nur Minuten";
    case "end_with_time":
      return "Enddatum mit Uhrzeit";
    default:
      return mode || "—";
  }
}

export function notifyLabel(notify: string): string {
  switch (notify) {
    case "self":
      return "die Person";
    case "self_and_proxy":
      return "Person und Vertretung";
    default:
      return "keine";
  }
}
